import { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../config/turnstile';

const SITE_KEY = TURNSTILE_SITE_KEY || undefined;
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

// Cloudflare Turnstile widget. Calls onVerify(token) when solved. If no site key
// is configured (VITE_TURNSTILE_SITE_KEY), renders nothing and emits an empty
// token so forms keep working until Turnstile is set up (the backend skips
// verification until TURNSTILE_SECRET_KEY is set too).
export function Turnstile({ onVerify, resetKey = 0 }: { onVerify: (token: string) => void; resetKey?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const cb = useRef(onVerify);
  cb.current = onVerify;

  // Turnstile tokens are single-use: after a submit (esp. a FAILED login) the token
  // is consumed, so the next attempt would be rejected with captcha_failed. Bumping
  // resetKey re-runs the widget to mint a fresh token (callback fires again).
  useEffect(() => {
    if (resetKey > 0 && widgetId.current && window.turnstile) {
      cb.current('');
      try { window.turnstile.reset(widgetId.current); } catch { /* noop */ }
    }
  }, [resetKey]);

  useEffect(() => {
    if (!SITE_KEY) { cb.current(''); return; }

    let cancelled = false;
    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current || widgetId.current) return;
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => cb.current(token),
        'error-callback': () => cb.current(''),
        'expired-callback': () => cb.current(''),
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID; s.src = SCRIPT_SRC; s.async = true; s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      const poll = setInterval(() => {
        if (window.turnstile) { clearInterval(poll); render(); }
      }, 150);
      setTimeout(() => clearInterval(poll), 8000);
    }

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
        widgetId.current = null;
      }
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className="my-3 flex justify-center" />;
}
